import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import AntiOfflineNotice from "@/components/locanest/AntiOfflineNotice";

// Client-side hint that mirrors the backend's offline-deal detector so users
// see the nudge as they type. Server still runs the authoritative flagging.
const OFFLINE_HINTS = [
  /\bcash\s+only\b/i, /\bcash\s+deal\b/i, /\bpay\s+in\s+cash\b/i,
  /\boffline\b/i, /\boutside\s+(the\s+)?platform\b/i, /\bwithout\s+booking\b/i,
  /\bcall\s+me\s+directly\b/i, /\bwhatsapp\s+me\b/i, /\bdm\s+me\b/i,
  /\bmeet\s+directly\b/i, /(?:\+?91[-\s]?)?[6-9]\d{9}/,
];
const looksOffline = (t) => t && OFFLINE_HINTS.some((r) => r.test(t));

/**
 * ChatDrawer
 * - When invoked as TENANT: pass `propertyId` — component POSTs /chats/open to create/upsert the chat.
 * - When invoked as OWNER (or with a known chat): pass `chatId` — skips open call.
 */
export default function ChatDrawer({ propertyId, chatId, open, onOpenChange, onOpened }) {
  const { user } = useAuth();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);
  const pollRef = useRef(null);
  const cursorRef = useRef(null); // last message created_at

  // Initialize chat: either fetch by chatId or open by propertyId
  useEffect(() => {
    if (!open || !user) return;
    setChat(null); setMessages([]); cursorRef.current = null;
    if (chatId) {
      api.get(`/chats/${chatId}`).then(({ data }) => { setChat(data); onOpened?.(data); })
        .catch(() => toast.error("Chat not found"));
    } else if (propertyId) {
      api.post("/chats/open", { property_id: propertyId })
        .then(({ data }) => { setChat(data); onOpened?.(data); })
        .catch((e) => {
          const detail = e.response?.data?.detail;
          if (detail === "No chats yet for this property") {
            toast.info("No chats yet for this property.");
            onOpenChange(false);
          } else {
            toast.error(detail || "Could not open chat");
          }
        });
    }
  }, [open, user, propertyId, chatId]);

  // Load + poll messages
  const load = async (id, since) => {
    try {
      const url = `/chats/${id}/messages${since ? `?since=${encodeURIComponent(since)}` : ""}`;
      const { data } = await api.get(url);
      if (data.length === 0) return;
      if (since) setMessages((prev) => [...prev, ...data]);
      else setMessages(data);
      cursorRef.current = data[data.length - 1].created_at;
    } catch {}
  };

  useEffect(() => {
    if (!chat) return;
    load(chat.id);
    pollRef.current = setInterval(() => load(chat.id, cursorRef.current), 5000);
    return () => clearInterval(pollRef.current);
  }, [chat]);

  useEffect(() => { bodyRef.current?.scrollTo?.({ top: bodyRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !chat) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chats/${chat.id}/messages`, { body: text });
      setMessages((prev) => [...prev, data]);
      cursorRef.current = data.created_at;
      setText("");
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  if (!open) return null;
  const otherName = chat ? (user?.id === chat.tenant_id ? chat.owner_name : chat.tenant_name) : "";

  return (
    <div className="fixed inset-0 z-50 pointer-events-none" data-testid="chat-drawer">
      <div className="absolute inset-0 bg-black/30 pointer-events-auto" onClick={() => onOpenChange(false)} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#FDFBF7] border-l border-[#1A1F2C]/10 shadow-2xl pointer-events-auto flex flex-col">
        <div className="p-4 border-b border-[#1A1F2C]/10 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#5C6B7A]">In-app chat</div>
            <div className="font-display text-lg truncate">{chat?.property_title || "Loading…"}</div>
            <div className="text-xs text-[#5C6B7A]">with {otherName}</div>
          </div>
          <button onClick={() => onOpenChange(false)} className="p-1.5 rounded hover:bg-[#F0EBE1]" aria-label="close" data-testid="chat-close"><X size={18} /></button>
        </div>

        <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-body">
          <AntiOfflineNotice />
          {messages.length === 0 ? (
            <div className="text-center text-sm text-[#5C6B7A] mt-10">Say hi to start the conversation.</div>
          ) : messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-[#C85A32] text-white rounded-br-sm" : "bg-[#F0EBE1] text-[#1A1F2C] rounded-bl-sm"} ${m.flagged ? "ring-2 ring-[#C85A32]/50" : ""}`}>
                  <div>{m.body}</div>
                  {m.flagged && (
                    <div className={`mt-1 text-[10px] ${mine ? "text-white/85" : "text-[#C85A32]"} font-semibold`} data-testid="msg-flag">
                      ⚠ Flagged as offline-deal push
                    </div>
                  )}
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#5C6B7A]"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-3 border-t border-[#1A1F2C]/10">
          {looksOffline(text) && (
            <div className="mb-2 text-[11px] rounded bg-[#F0EBE1] border border-[#C85A32]/30 px-2 py-1.5 text-[#1A1F2C]" data-testid="chat-offline-warn">
              ⚠ Complete deals through LocaNest — messages that push offline get flagged automatically.
            </div>
          )}
          <div className="flex gap-2">
            <input
              className="flex-1 h-10 px-3 border border-[#1A1F2C]/15 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C85A32]/40 bg-white"
              placeholder="Type a message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              data-testid="chat-input"
            />
            <Button onClick={send} disabled={sending || !text.trim() || !chat} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="chat-send">
              <Send size={15} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
